import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AuthService } from './auth.service';
import { Bridge, BridgeService } from './bridge.service';
import { BridgeDialogComponent } from './bridge-dialog.component';
import { PlotEditorDialogComponent, PlotEditorData } from './plot-editor-dialog.component';
import { QueryBuilderDialogComponent } from './query-builder-dialog.component';
import { Plot, PlotDraft, PlotTab, emptyDraft } from './plot.models';
import { PlotsService } from './plots.service';

type AppState = 'pick' | 'authenticating' | 'ready' | 'error';

/** Per-project memory of which tab was open; the tabs themselves live in pyst. */
const ACTIVE_TAB_KEY_PREFIX = 'plots-active-tab:';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
    public tabs: PlotTab[] = [];
    public activeTabId = '';
    public loading = true;
    /**
     * Where the shell is: choosing a project, mid-login, running, or showing why a
     * login failed. Starting at 'pick' unless this session already confirmed a
     * project is what prevents auto-joining a realm the user did not ask for — and
     * gives them somewhere to go when the stored one no longer exists.
     */
    public state: AppState = 'pick';
    public authError: string | null = null;
    /** Project highlighted in the picker; defaults to the last active one. */
    public pickedId: string | null = null;

    constructor(
        private plotsService: PlotsService,
        private bridgeService: BridgeService,
        private auth: AuthService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
    ) {}

    ngOnInit(): void {
        this.pickedId = this.activeBridge?.id ?? null;

        // Returning from the Keycloak redirect, or reloading mid-session: the project
        // was confirmed earlier in this browser session, so continue into it.
        const confirmed = this.auth.confirmedBridgeId;
        const bridge = confirmed ? this.bridges.find((entry) => entry.id === confirmed) : undefined;
        if (bridge) {
            void this.signIn(bridge);
        }
    }

    /** From the picker: remember the choice for this session, then sign in. */
    choose(bridge: Bridge): void {
        this.pickedId = bridge.id;
        this.bridgeService.activate(bridge.id);
        this.auth.confirmBridge(bridge.id);
        void this.signIn(bridge);
    }

    /** Leave a failed or unwanted realm and go back to choosing. */
    backToPicker(): void {
        this.auth.clearConfirmedBridge();
        this.authError = null;
        this.state = 'pick';
    }

    private async signIn(bridge: Bridge): Promise<void> {
        this.state = 'authenticating';
        this.authError = null;
        try {
            const ok = await this.auth.init(bridge);
            if (!ok) {
                throw new Error('Keycloak did not authenticate');
            }
            this.state = 'ready';
            await this.load();
        } catch (error) {
            // Do not leave the confirmed project set: on reload that would loop back
            // into the same failing realm, which is exactly the stuck state to avoid.
            console.error('Sign-in failed', error);
            this.auth.clearConfirmedBridge();
            this.authError = `Could not sign in to realm "${bridge.keycloakRealm}". `
                + 'The realm may have been removed or Keycloak may be unreachable.';
            this.state = 'error';
        }
    }

    /** Null until a project is chosen; the ready state always has one. */
    get activeBridge(): Bridge | null {
        return this.bridgeService.activeBridge;
    }

    /** The signed-in project. Only meaningful once `state` is 'ready'. */
    private get project(): Bridge {
        const bridge = this.bridgeService.activeBridge;
        if (!bridge) {
            throw new Error('no project is active');
        }
        return bridge;
    }

    get bridges(): Bridge[] {
        return this.bridgeService.bridges;
    }

    get username(): string {
        return this.auth.username;
    }

    get activeTab(): PlotTab | undefined {
        return this.tabs.find((tab) => tab.id === this.activeTabId);
    }

    get plots(): Plot[] {
        return this.activeTab?.plots ?? [];
    }

    async load(): Promise<void> {
        this.loading = true;
        try {
            this.tabs = await this.plotsService.load();
            const remembered = this.readActiveTab();
            this.activeTabId = this.tabs.some((tab) => tab.id === remembered) ? remembered! : this.tabs[0].id;
        } catch (error) {
            this.fail(`Could not read plots from ${this.project.apiPath}`, error);
        } finally {
            this.loading = false;
        }
    }

    /**
     * Switching realm means a new Keycloak instance, so reload — but confirm the new
     * project first: the user chose it explicitly, so the reload goes straight into
     * its login rather than back to the picker.
     */
    switchBridge(bridge: Bridge): void {
        if (bridge.id !== this.activeBridge?.id) {
            this.auth.confirmBridge(bridge.id);
            this.bridgeService.switchAndReload(bridge.id);
        }
    }

    manageBridges(): void {
        this.dialog.open(BridgeDialogComponent, {
            data: { bridges: this.bridges, activeId: this.activeBridge?.id ?? null },
            autoFocus: false,
        });
    }

    /** Build a query from a datastream and a window, then open it in the editor so it
     * can be adjusted before saving. */
    openBuilder(plot?: Plot): void {
        this.dialog
            // Reopening a plot hands the builder the selection it was made from, so
            // a stream can be added without editing the pyst text by hand.
            .open(QueryBuilderDialogComponent, { autoFocus: false, data: plot?.builder ?? null })
            .afterClosed()
            .subscribe((draft: PlotDraft | undefined) => {
                if (!draft) {
                    return;
                }
                const title = plot ? 'Edit plot' : `Add plot to ${this.project.name}`;
                // Keep the plot's own name and layout; only what the builder
                // decided is replaced.
                const next = plot
                    ? { ...draft, name: plot.name, cols: plot.cols, height: plot.height }
                    : draft;
                this.openEditorWithDraft(next, title, plot);
            });
    }

    openEditor(plot?: Plot): void {
        const data: PlotEditorData = {
            title: plot ? 'Edit plot' : `Add plot to ${this.project.name}`,
            draft: plot ? this.toDraft(plot) : emptyDraft(),
        };

        this.openEditorWithDraft(data.draft, data.title, plot);
    }

    private openEditorWithDraft(draft: PlotDraft, title: string, plot?: Plot): void {
        this.dialog
            .open(PlotEditorDialogComponent, { data: { title, draft }, autoFocus: false })
            .afterClosed()
            .subscribe(async (saved: PlotDraft | undefined) => {
                if (!saved) {
                    return;
                }
                const next = plot
                    ? this.plots.map((entry) => (entry.id === plot.id ? { ...entry, ...saved } : entry))
                    : [...this.plots, this.plotsService.newPlot(saved)];
                await this.persist(next, 'Saving the plot failed');
            });
    }

    async saveCols(event: { plot: Plot; cols: number }): Promise<void> {
        const next = this.plots.map((entry) =>
            entry.id === event.plot.id ? { ...entry, cols: event.cols } : entry,
        );
        // Apply immediately so the card moves on the click, not after the round trip
        // to pyst; a failed save puts the previous layout back.
        await this.persist(next, 'Saving the width failed', true);
    }

    async remove(plot: Plot): Promise<void> {
        if (!confirm(`Delete "${plot.name}"?`)) {
            return;
        }
        await this.persist(
            this.plots.filter((entry) => entry.id !== plot.id),
            'Delete failed',
        );
    }

    logout(): void {
        this.auth.logout();
    }

    selectTab(tab: PlotTab): void {
        this.activeTabId = tab.id;
        this.writeActiveTab(tab.id);
    }

    async addTab(): Promise<void> {
        const name = prompt('Name for the new tab', `Tab ${this.tabs.length + 1}`);
        if (name === null) {
            return;
        }
        const tab = this.plotsService.newTab(name);
        if (await this.persistTabs([...this.tabs, tab], 'Adding the tab failed')) {
            this.selectTab(tab);
        }
    }

    async renameTab(tab: PlotTab): Promise<void> {
        const name = prompt('Rename tab', tab.name);
        if (name === null || !name.trim() || name.trim() === tab.name) {
            return;
        }
        await this.persistTabs(
            this.tabs.map((entry) => (entry.id === tab.id ? { ...entry, name: name.trim() } : entry)),
            'Renaming the tab failed',
        );
    }

    async removeTab(tab: PlotTab): Promise<void> {
        const count = tab.plots.length;
        const warning = count ? ` and its ${count} plot${count === 1 ? '' : 's'}` : '';
        if (!confirm(`Delete tab "${tab.name}"${warning}?`)) {
            return;
        }
        const remaining = this.tabs.filter((entry) => entry.id !== tab.id);
        // A project always shows at least one tab, so deleting the last one empties it.
        const next = remaining.length ? remaining : [this.plotsService.newTab('Plots')];
        if (await this.persistTabs(next, 'Deleting the tab failed') && tab.id === this.activeTabId) {
            this.selectTab(next[0]);
        }
    }

    trackById(_index: number, entry: { id: string }): string {
        return entry.id;
    }

    /** Replace the active tab's plot list, write everything back to pyst, then adopt it. */
    private async persist(plots: Plot[], failureMessage: string, optimistic = false): Promise<void> {
        const next = this.tabs.map((tab) => (tab.id === this.activeTabId ? { ...tab, plots } : tab));
        await this.persistTabs(next, failureMessage, optimistic);
    }

    /**
     * Write to pyst and adopt locally. Optimistic adopts first and rolls back if the
     * write fails; the default waits, which is right for edits that refetch a plot.
     */
    private async persistTabs(tabs: PlotTab[], failureMessage: string, optimistic = false): Promise<boolean> {
        const previous = this.tabs;
        if (optimistic) {
            this.tabs = tabs;
        }
        try {
            await this.plotsService.save(tabs);
            this.tabs = tabs;
            return true;
        } catch (error) {
            if (optimistic) {
                this.tabs = previous;
            }
            this.fail(failureMessage, error);
            return false;
        }
    }

    private readActiveTab(): string | null {
        try {
            return localStorage.getItem(ACTIVE_TAB_KEY_PREFIX + this.project.id);
        } catch {
            return null;
        }
    }

    private writeActiveTab(id: string): void {
        try {
            localStorage.setItem(ACTIVE_TAB_KEY_PREFIX + this.project.id, id);
        } catch {
            // Remembering the tab is a convenience; nothing depends on it.
        }
    }

    private toDraft(plot: Plot): PlotDraft {
        return {
            name: plot.name,
            query: plot.query,
            cols: plot.cols,
            height: plot.height,
            notes: plot.notes,
        };
    }

    private fail(message: string, error: unknown): void {
        console.error(message, error);
        this.snackBar.open(message, 'Dismiss', { duration: 6000 });
    }
}
